// App-wide toast notifications — non-blocking, slide in from the right and
// auto-dismiss after ~5s. Reusable via the useToast() hook (#6).
//
//   const toast = useToast()
//   toast.success('Techniker angelegt.')
//   toast.error('Etwas ist schiefgelaufen.')
//
// useToast() returns a no-op API when no ToastProvider is mounted, so components
// (and unit tests) never crash for lack of a provider.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Icon } from './icons'
import { registerGlobalToast } from '../lib/globalToast'

export type ToastTone = 'success' | 'error' | 'info'

interface ToastItem {
  id: number
  message: string
  tone: ToastTone
}

export interface ToastApi {
  show: (message: string, tone?: ToastTone) => void
  success: (message: string) => void
  error: (message: string) => void
  info: (message: string) => void
}

const NOOP: ToastApi = { show: () => {}, success: () => {}, error: () => {}, info: () => {} }

const ToastContext = createContext<ToastApi | null>(null)

export function useToast(): ToastApi {
  return useContext(ToastContext) ?? NOOP
}

const TONE: Record<ToastTone, { fg: string; bg: string; border: string }> = {
  success: { fg: 'var(--viridian)', bg: 'var(--st-online-soft)', border: 'var(--st-online-line)' },
  error: { fg: 'var(--st-offline)', bg: 'var(--st-offline-soft)', border: 'var(--coral-line)' },
  info: { fg: 'var(--ink-70)', bg: 'var(--surface-2)', border: 'var(--line)' },
}

function ToastCard({ item, onClose }: { item: ToastItem; onClose: () => void }) {
  const t = TONE[item.tone]
  const icon = item.tone === 'success' ? <Icon.check size={16} /> : item.tone === 'error' ? <Icon.xcircle size={16} /> : <Icon.info size={16} />
  return (
    <div
      role="status"
      style={{
        pointerEvents: 'auto',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        minWidth: 260,
        maxWidth: 380,
        padding: '12px 14px',
        borderRadius: 'var(--r-sm)',
        background: t.bg,
        border: `1px solid ${t.border}`,
        color: 'var(--ink)',
        boxShadow: 'var(--shadow-md)',
        animation: 'vt-toast-in .25s ease',
      }}
    >
      <span style={{ color: t.fg, flex: '0 0 auto', display: 'inline-flex' }}>{icon}</span>
      <span style={{ fontSize: 13.5, lineHeight: 1.4, flex: 1 }}>{item.message}</span>
      <button
        type="button"
        aria-label="Schließen"
        onClick={onClose}
        style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--ink-50)', padding: 0, display: 'inline-flex' }}
      >
        <Icon.block size={14} />
      </button>
    </div>
  )
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const nextId = useRef(1)

  const remove = useCallback((id: number) => setToasts((cur) => cur.filter((t) => t.id !== id)), [])

  const show = useCallback(
    (message: string, tone: ToastTone = 'info') => {
      const id = nextId.current++
      setToasts((cur) => [...cur, { id, message, tone }])
      setTimeout(() => remove(id), 5000)
    },
    [remove],
  )

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (m) => show(m, 'success'),
      error: (m) => show(m, 'error'),
      info: (m) => show(m, 'info'),
    }),
    [show],
  )

  // Befund 35: API für React-freien Code (MutationCache-Fehler-Toast) bereitstellen.
  useEffect(() => {
    registerGlobalToast(api)
    return () => registerGlobalToast(null)
  }, [api])

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* The portal sits as a sibling of <App/>, i.e. OUTSIDE the `.vitals` wrapper
          that AppShell renders. Without `.vitals` here the design system (IBM Plex
          Sans font + all `var(--…)` tokens, both scoped under `.vitals`) does not
          apply and toasts fall back to ungestyled browser defaults (#108). */}
      <div
        className="vitals"
        style={{
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: 2000,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          pointerEvents: 'none',
          // `.vitals` brings the design tokens + font, but its base rule also sets
          // `background: var(--page)` and `height: 100%` — on this fixed container
          // that would paint a full-height strip. Neutralise both inline.
          background: 'transparent',
          height: 'auto',
        }}
      >
        {toasts.map((t) => (
          <ToastCard key={t.id} item={t} onClose={() => remove(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}
